import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getSignedMediaUrl } from '@/lib/utils';

interface CarouselItem {
  id: string;
  type: 'video' | 'image';
  url: string;
}

interface CustomCarouselProps {
  items: CarouselItem[];
}

const CustomCarousel: React.FC<CustomCarouselProps> = ({ items }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = () => {
    const isFirstSlide = currentIndex === 0;
    const newIndex = isFirstSlide ? items.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
  };

  const goToNext = () => {
    const isLastSlide = currentIndex === items.length - 1;
    const newIndex = isLastSlide ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
  };
  
  const goToSlide = (slideIndex: number) => {
    setCurrentIndex(slideIndex);
  };

  if (!items || items.length === 0) {
    return (
      <div className="group relative h-full w-full aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl shadow-blue-500/10 bg-gray-800 flex items-center justify-center">
        <span className="text-gray-500 text-sm">No media available</span>
      </div>
    );
  }

  return (
    <div className="group relative h-full w-full aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl shadow-blue-500/10">
      {/* Slides Container */}
      <div
        className="flex transition-transform ease-in-out duration-500 h-full"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {items.map((item) => (
          <div key={item.id} className="relative flex-shrink-0 w-full h-full bg-black">
            {item.type === 'video' ? (
              <video 
                src={getSignedMediaUrl(item.url, 'video').url} 
                autoPlay 
                loop 
                muted 
                playsInline 
                className="w-full h-full object-cover" 
              />
            ) : (
              <img 
                src={getSignedMediaUrl(item.url, 'image').url} 
                alt="Event gallery" 
                className="w-full h-full object-cover" 
              />
            )}
          </div>
        ))}
      </div>

      {/* Navigation Arrows (Appear on hover) */}
      {items.length > 1 && (
        <div className="absolute inset-0 flex items-center justify-between p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <button 
            onClick={goToPrevious} 
            className="p-2 rounded-full bg-black/50 hover:bg-black/75 transition text-white"
          >
            <ChevronLeft size={28} />
          </button>
          <button 
            onClick={goToNext} 
            className="p-2 rounded-full bg-black/50 hover:bg-black/75 transition text-white"
          >
            <ChevronRight size={28} />
          </button>
        </div>
      )}

      {/* Pagination Dots */}
      {items.length > 1 && (
        <div className="absolute bottom-5 left-0 right-0 flex justify-center gap-2">
          {items.map((_, slideIndex) => (
            <button
              key={slideIndex}
              onClick={() => goToSlide(slideIndex)}
              className={`h-2 rounded-full transition-all duration-500 ${
                currentIndex === slideIndex ? 'w-6 bg-white' : 'w-2 bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to slide ${slideIndex + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomCarousel;
